import { initializeRouterWithFeatureFlags } from './router/initializeRouterWithFeatureFlags';
import { InvalidSessionError } from '@/@core/models/api/errors';
import { ASSISTANT_SERVICE } from '@/@core/services/api/AssistantService';
import { handleFatalAppError } from '@/@core/services/auth/errorRecovery';
import { FEATURE_CLIENT } from '@/@core/services/feature-flag/FeatureClient';
import { getFaroApiKey } from '@/@core/utils/getFaroApiKey';
import { appReady } from '@/appErrorState';
import { setupAnalytics } from '@/plugins/analytics';
import { setupProductTour, productTour } from '@/plugins/productTour';
import { setupTelemetry } from '@/plugins/telemetry';
import {
  BAGGAGE_KEYS,
  BaggageScope,
  telemetry,
} from '@aleph-alpha/lib-telemetry';
import type { App } from 'vue';

/**
 * Registers the router, feature flags, and post-router plugins on the given app instance.
 * Call after Pinia/i18n/design system are already installed on `app`.
 */
async function bootstrapApplication(app: App): Promise<void> {
  const featureFlags = await ASSISTANT_SERVICE.featureFlags();
  FEATURE_CLIENT.setFlags(featureFlags);
  FEATURE_CLIENT.setFeatureSet(
    (await ASSISTANT_SERVICE.featureSet()).feature_set,
  );
  const router = await initializeRouterWithFeatureFlags(FEATURE_CLIENT);

  app.use(router);
  router.isReady().then(async () => {
    const faroApiKey = await getFaroApiKey();
    await setupTelemetry(app, faroApiKey);
    await setupAnalytics(app, faroApiKey);
    await setupProductTour(app, true);

    productTour.addTags({
      environment: import.meta.env.MODE,
    });
  });

  const enabledFeatures = Object.entries(featureFlags)
    .filter(([, enabled]) => Boolean(enabled))
    .map(([featureName]) => featureName);

  if (enabledFeatures.length > 0) {
    telemetry.setBaggage(
      BAGGAGE_KEYS.FEATURE_FLAGS,
      enabledFeatures.join(','),
      BaggageScope.SESSION,
    );
  }
}

/**
 * Runs bootstrap from the root component lifecycle; sets {@link appReady} or {@link appError}.
 */
export async function bootstrapApp(vueApp: App): Promise<void> {
  try {
    await bootstrapApplication(vueApp);
    appReady.value = true;
  } catch (e) {
    await handleFatalAppError(
      e instanceof InvalidSessionError ? 'session' : 'config',
    );
  }
}
